import React, {useEffect, useMemo, useState} from "react";
import {dateComparator, numComparator, stringComparator} from "../../../../core/services/utils/utils";
import classnames from "classnames";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, DefaultDataGridPageSizes, useDataGridSavedState} from "../../../../packages/data-grid";
import PartnerDataGrid from "../../../containers/data-grid";
import {AvailableCreditsDataGridColumnNames} from "../../../../core/constants/table/data-grid-column-keys";
import Button from "../../base/button/button";

const _initialPaginationInfo = {
    pageSize: 10,
    currentPage: 1,
    length: 0,
    sizes: DefaultDataGridPageSizes,
};

const AvailableCreditsTable = ({
                                   loading,
                                   credits,
                                   onSelect,
                                   selectingCreditId,
                                   selectedCreditTransactionNos,
                                   className,
                                   excludedColumns,
                                   hideExporting = true,
                                   getExportInfo,
                                   saveConfig,
                                   ...dataGridProps
                               }) => {
    const [visibleCredits, setVisibleCredits] = useState([]);

    const columns = useMemo(() =>
            /**@type {DataGridColumn[]}*/
            [
                {
                    title: 'Transaction No',
                    name: AvailableCreditsDataGridColumnNames.transactionNo,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 170,
                    pinned: false,
                },
                {
                    title: 'Reference',
                    name: AvailableCreditsDataGridColumnNames.reference,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.string,
                },
                {
                    title: 'Date',
                    name: AvailableCreditsDataGridColumnNames.date,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.date,
                },
                {
                    title: 'Type',
                    name: AvailableCreditsDataGridColumnNames.type,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                },
                {
                    title: 'Credit',
                    name: AvailableCreditsDataGridColumnNames.credit,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.money,
                },
                {
                    title: 'Balance',
                    name: AvailableCreditsDataGridColumnNames.balance,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.money,
                    pinned: true,
                    pinnedType: DataGridColumnPinnedTypes.right,
                },
                {
                    name: AvailableCreditsDataGridColumnNames.select,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    pinned: true,
                    pinnedToggleable: false,
                    pinnedType: DataGridColumnPinnedTypes.right,
                },
            ].filter(e => !excludedColumns?.includes(e.name))
        , [excludedColumns])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: saveConfig?.name,
        version: saveConfig?.version,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ..._initialPaginationInfo,
        pageSize: dataGridPageSize
    } : _initialPaginationInfo, [dataGridPageSize]);

    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);
    const [orderBy, setOrderBy] = useState(dataGridSortBy);

    /**@type {function(a, b): number}*/
    const orderByMethod = useMemo(() => {
        switch (orderBy?.field) {
            case AvailableCreditsDataGridColumnNames.transactionNo:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.transactionNo ?? '', b?.transactionNo ?? '')
                    : stringComparator(b?.transactionNo ?? '', a?.transactionNo ?? '')
            case AvailableCreditsDataGridColumnNames.reference:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.referenceDisplay ?? '', b?.referenceDisplay ?? '')
                    : stringComparator(b?.referenceDisplay ?? '', a?.referenceDisplay ?? '')
            case AvailableCreditsDataGridColumnNames.date:
                return (a, b) => orderBy.descending
                    ? dateComparator(a?.submittedDate ?? '', b?.submittedDate ?? '')
                    : dateComparator(b?.submittedDate ?? '', a?.submittedDate ?? '')
            case AvailableCreditsDataGridColumnNames.type:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.transactionTypeDescription ?? '', b?.transactionTypeDescription ?? '')
                    : stringComparator(b?.transactionTypeDescription ?? '', a?.transactionTypeDescription ?? '')
            case AvailableCreditsDataGridColumnNames.credit:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.amount ?? 0, b?.amount ?? 0)
                    : numComparator(b?.amount ?? 0, a?.amount ?? 0)
            case AvailableCreditsDataGridColumnNames.balance:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.balance ?? 0, b?.balance ?? 0)
                    : numComparator(b?.balance ?? 0, a?.balance ?? 0)

            default:
                return (a, b) => 0
        }
    }, [orderBy]);

    /**
     * With each change in the [credits] and [paginationInfo] values of the state:
     * - selects a slice of the credits to be set as the visible credits.
     */
    useEffect(() => {
        if (!credits?.length) {
            setPaginationInfo(initialPaginationInfo);
            return setVisibleCredits([]);
        }
        const start = Math.min(Math.max((paginationInfo.currentPage - 1) * paginationInfo.pageSize, 0), credits.length);
        const end = Math.min((paginationInfo.currentPage) * paginationInfo.pageSize, credits.length);
        const chunk = Array.from(credits).sort(orderByMethod).slice(start, end)
        setPaginationInfo(prevState => ({...prevState, length: credits.length}));
        setVisibleCredits(chunk);
    }, [credits, paginationInfo.currentPage, paginationInfo.pageSize, orderByMethod, initialPaginationInfo])

    /**
     * Sorts the entries of the credits in place.
     * @param {DataGridSortBy} tableOrderBy
     */
    const onSort = (tableOrderBy) => {
        setOrderBy(tableOrderBy);
        setPaginationInfo(prevState => ({...prevState, currentPage: 1}));
    }

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return visibleCredits
                ?.map((credit) => {
                    const selecting = selectingCreditId === credit.id;
                    const selected = selectedCreditTransactionNos?.includes(credit.transactionNo) ?? false;
                    return {
                        key: credit.id,
                        data: credit,
                        cells: {
                            [AvailableCreditsDataGridColumnNames.transactionNo]: credit?.transactionNo,
                            [AvailableCreditsDataGridColumnNames.reference]: credit?.referenceDisplay,
                            [AvailableCreditsDataGridColumnNames.date]: credit?.submittedDate,
                            [AvailableCreditsDataGridColumnNames.type]: credit?.transactionTypeDescription,
                            [AvailableCreditsDataGridColumnNames.credit]: credit?.amount,
                            [AvailableCreditsDataGridColumnNames.balance]: credit?.balance,
                            [AvailableCreditsDataGridColumnNames.select]:
                                <Button
                                    disabled={!!selectingCreditId}
                                    variant={selected ? 'contained' : 'outlined'}
                                    color={'primary'}
                                    onClick={() => onSelect(credit, selected)}
                                >
                                    {
                                        selecting
                                            ? 'processing...'
                                            : selected
                                                ? "Remove"
                                                : "Apply"
                                    }
                                </Button>,
                        }
                    }
                })
            ?? []
    }, [visibleCredits, selectedCreditTransactionNos, selectingCreditId, onSelect])


    return (
        <>
            <PartnerDataGrid
                {...dataGridProps}
                hideFooterActions
                hideExporting={hideExporting}
                getExportInfo={getExportInfo}
                {...(saveConfig ?? {})}
                initialLayoutState={{
                    columns: columns,
                }}
                classNames={{
                    container: classnames(className),
                }}
                rows={dataGridRows}
                columns={dataGridColumns}
                state={{
                    loading: {state: loading},
                    sortBy: orderBy,
                    pagination: paginationInfo,
                    density: dataGridDensity,
                }}
                onSortByChanged={(_, current) => onSort(current)}
                onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                onPageSizeChanged={(_, current) => setPaginationInfo(current)}
            />
        </>
    )
}

export default AvailableCreditsTable;
